import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { authAPI } from './lib/api';
import { LOCAL_FIRST, connectPowerSync, disconnectPowerSync } from './lib/powersync/db';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import OAuthConsent from './pages/OAuthConsent';
import InviteAccept from './pages/InviteAccept';
import ShowDashboard from './pages/ShowDashboard';
import RundownEditor from './pages/RundownEditor';
import PrintRundown from './pages/PrintRundown';
import Pricing from './pages/Pricing';
import Billing from './pages/Billing';
import Account from './pages/Account';
import Admin from './pages/Admin';
import HelpPage from './pages/HelpPage';
import ToolsLayout from './components/layout/ToolsLayout';
import ToolsIndex from './pages/ToolsIndex';
import StandingsUpdaterPage from './pages/StandingsUpdaterPage';
import AthleteImporterPage from './pages/AthleteImporterPage';
import SeriesStandingsPage from './pages/SeriesStandingsPage';
import VmixScriptEditorPage from './pages/VmixScriptEditorPage';
import ClimbBridgePage from './pages/ClimbBridgePage';
import TimingBridgePage from './pages/TimingBridgePage';
import PreviewSyncPage from './pages/PreviewSyncPage';
import Gym from './pages/Gym';
import FleetLayout from './components/layout/FleetLayout';
import FleetPage from './pages/FleetPage';
import FleetBoxPage from './pages/FleetBoxPage';
import ScoringPage from './pages/ScoringPage';
import ScoringCompetitionPage from './pages/ScoringCompetitionPage';
import { BroadcastTimingPage, SharedTimingPage } from './pages/BroadcastTimingPage';

// The scoring screens load on their own: the venue server serves this build,
// and nobody else needs the code. The same screens for a competition run in
// Mission Control are at /scoring/<id>/judge and so on.
const Bcs = lazy(() => import('./bcs/Bcs'));
const CloudScreens = lazy(() => import('./bcs/Bcs').then(m => ({ default: m.CloudScreens })));

function ProtectedRoute({ children }) {
  const token = useAuthStore(state => state.token);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function PublicOnlyRoute({ children }) {
  const token = useAuthStore(state => state.token);

  if (token) {
    return <Navigate to="/" replace />;
  }

  return children;
}

export default function App() {
  const { token, setAuth } = useAuthStore();

  // Refresh the session on startup — picks up role and capabilities in the open
  // workspace, and moves someone whose access ended to a workspace they can reach.
  useEffect(() => {
    if (!token) return;
    authAPI.me().then(res => {
      const { token: newToken, ...user } = res.data;
      setAuth(newToken || token, user);
    }).catch(() => {});
  }, []);

  // Local-first: open the sync connection once authenticated, close it on logout.
  useEffect(() => {
    if (!LOCAL_FIRST) return;
    if (token) {
      connectPowerSync().catch(err => console.error('PowerSync connect failed:', err));
    } else {
      disconnectPowerSync().catch(() => {});
    }
  }, [token]);

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicOnlyRoute>
              <Login />
            </PublicOnlyRoute>
          }
        />
        <Route path="/auth/callback" element={<AuthCallback />} />
        {/* OAuth consent for remote MCP connectors. Not wrapped in
            ProtectedRoute: it needs to stash the OAuth parameters before
            bouncing to login, or they are lost on the way back. */}
        <Route path="/oauth/authorize" element={<OAuthConsent />} />
        {/* Invite links. Public: the page explains the invite before anyone has
            signed in, and carries the token through sign-in or sign-up. */}
        <Route path="/invite/:token" element={<InviteAccept />} />
        {/* Read-only broadcast timing: by share link (no login), and in-app. */}
        <Route path="/timing/:token" element={<SharedTimingPage />} />
        <Route
          path="/broadcast/timing/:showId"
          element={
            <ProtectedRoute>
              <BroadcastTimingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <ShowDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shows/:id"
          element={
            <ProtectedRoute>
              <RundownEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shows/:id/print"
          element={
            <ProtectedRoute>
              <PrintRundown />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tools"
          element={
            <ProtectedRoute>
              <ToolsLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<ToolsIndex />} />
          <Route path="standings-updater" element={<StandingsUpdaterPage />} />
          <Route path="athlete-importer" element={<AthleteImporterPage />} />
          <Route path="series-standings" element={<SeriesStandingsPage />} />
          <Route path="vmix-script-editor" element={<VmixScriptEditorPage />} />
          <Route path="climb-bridge" element={<ClimbBridgePage />} />
          <Route path="timing-bridge" element={<TimingBridgePage />} />
          <Route path="preview-sync" element={<PreviewSyncPage />} />
        </Route>
        {/* The account's CamNode boxes: a section of its own, not a tool. */}
        <Route
          path="/fleet"
          element={
            <ProtectedRoute>
              <FleetLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<FleetPage />} />
          <Route path=":id" element={<FleetBoxPage />} />
        </Route>
        {/* A private microsite for two people, made for a phone home screen. It signs in for itself. */}
        <Route path="/gym/*" element={<Gym />} />
        <Route path="/bcs/*" element={<Suspense fallback={null}><Bcs /></Suspense>} />
        {/* Climbing competitions. The page needs a sign-in; the screens under it use a PIN instead. */}
        <Route
          path="/scoring"
          element={
            <ProtectedRoute>
              <FleetLayout
                section="scoring"
                capability="production:edit"
                denied={<div className="max-w-xl mx-auto px-6 py-16 text-center text-sm text-gray-400">Scoring is for people who can edit production in this workspace.</div>}
              />
            </ProtectedRoute>
          }
        >
          <Route index element={<ScoringPage />} />
          <Route path=":competitionId" element={<ScoringCompetitionPage />} />
        </Route>
        <Route path="/scoring/:competitionId/*" element={<Suspense fallback={null}><CloudScreens /></Suspense>} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <Account />
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute>
              <Billing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <Admin />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
